import React from 'react';
import { WifiOff, RefreshCcw, Bell, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useSync } from '@/hooks/useSync';
import { useNotifications } from '@/hooks/useNotifications';
import { useTranslation } from '@/lib/i18n';
import Footer from './Footer';
import SettingsMenu from './SettingsMenu';
import CookieConsent from '../CookieConsent';
import FooterNav from './FooterNav';
import ShareApp from '../ShareApp';
import LanguageToggle from './LanguageToggle';
import Logo from '../common/Logo';
import { useTheme } from '@/lib/ThemeContext';

interface ShellProps {
    children: React.ReactNode;
}

export default function Shell({ children }: ShellProps) {
    const { isOnline, isSyncing, pendingCount } = useSync();
    const { unreadCount, markAsRead } = useNotifications();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { themeMode } = useTheme();

    return (
        <div className={`flex flex-col min-h-screen transition-colors duration-300 ${
            themeMode === 'v2' ? 'bg-[#0b0f1a]' : 'bg-slate-50'
        }`}>
            {/* Header */}
            <header className={`${themeMode === 'v2' ? 'bg-[#111827]/80 backdrop-blur-md border-slate-800 text-white shadow-xl' : 'bg-white border-slate-200 text-slate-900 shadow-sm'} border-b sticky top-0 z-50 px-4 py-3 flex items-center justify-between transition-all duration-300`}>
                <div
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => navigate('/')}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/'); }}
                    tabIndex={0}
                    role="button"
                    aria-label="Go to homepage"
                >
                    <motion.div
                        animate={{
                            y: [0, -8, 0],
                            rotate: [0, 2, 0]
                        }}
                        transition={{
                            duration: 4,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                        className="w-12 h-12 flex items-center justify-center drop-shadow-md"
                    >
                        <Logo className="w-full h-full" />
                    </motion.div>
                    <h1 className={`text-lg font-semibold animate-stagger-2 ${themeMode === 'v2' ? 'text-white' : 'text-slate-900'}`}>TipsNorge</h1>
                </div>
                <div className="flex items-center gap-2">
                    {isSyncing && (
                        <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider animate-pulse ${
                            themeMode === 'v2' ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-600'
                        }`}>
                            <RefreshCcw className="w-3 h-3 animate-spin" />
                            {t('common.syncing')} {pendingCount > 0 ? `(${pendingCount})` : ''}
                        </div>
                    )}
                    {!isOnline && (
                        <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                            themeMode === 'v2' ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500'
                        }`}>
                            <WifiOff className="w-3 h-3" />
                            {t('common.offline')}
                        </div>
                    )}

                    <button
                        onClick={() => {
                            markAsRead();
                            navigate('/track');
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { markAsRead(); navigate('/track'); } }}
                        className={`relative p-2 transition-colors ${
                            themeMode === 'v2' ? 'text-slate-400 hover:text-blue-500' : 'text-slate-500 hover:text-blue-600'
                        }`}
                        title={t('nav.notifications')}
                        aria-label={t('nav.notifications')}
                    >
                        <Bell className="w-5 h-5" />
                        {unreadCount > 0 && (
                            <span className="absolute top-1 right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white animate-pulse" />
                        )}
                    </button>

                    <ShareApp variant="icon" />

                    <button
                        onClick={() => navigate('/docs')}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/docs'); }}
                        className={`p-2 transition-colors ${
                            themeMode === 'v2' ? 'text-slate-400 hover:text-blue-500' : 'text-slate-500 hover:text-blue-600'
                        }`}
                        title={t('nav.help')}
                        aria-label={t('nav.help')}
                    >
                        <HelpCircle className="w-5 h-5" />
                    </button>

                    <div className="hidden sm:block">
                        <LanguageToggle />
                    </div>

                    <SettingsMenu />
                </div>
            </header>

            {/* Main Content */}
            <main
                id="main-content"
                className="flex-1 w-full max-w-5xl mx-auto p-4 pb-20 animate-fade-in-up"
            >
                {children}
                <Footer />
            </main>

            {/* Footer Navigation */}
            <FooterNav />
            <CookieConsent />
        </div>
    );
}
